/**
 * 🐞 Issues — the internal queue of in-app tool feedback (bugs + feature requests) filed via the
 * floating 💬 Report button. Internal staff only. Each row shows the reporter's note, the page they
 * were on, and their annotated screenshot; staff can mark items resolved or declined.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listToolFeedback, resolveToolFeedback, type ToolFeedback } from '../lib/api';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';

type Status = 'open' | 'resolved' | 'declined';

function Row({ fb, onChanged }: { fb: ToolFeedback; onChanged: () => void }) {
  const [zoom, setZoom] = useState(false);
  const [note, setNote] = useState('');
  const resolve = useMutation({
    mutationFn: (d: 'resolved' | 'declined') => resolveToolFeedback(fb.id, d, note.trim() || undefined),
    onSuccess: onChanged,
  });
  const when = new Date(fb.created_at).toLocaleString();
  return (
    <div className="card">
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className={`pill ${fb.kind === 'bug' ? 'pill-bad' : 'pill-stage'}`}>
          {fb.kind === 'bug' ? '🐞 Bug · 问题' : '✨ Feature · 建议'}
        </span>
        <strong>{fb.created_by_name ?? '—'}</strong>
        <span className="muted small">{when}</span>
        {fb.page_url && <span className="muted small">· {fb.page_url}</span>}
        {fb.status !== 'open' && (
          <span className={`pill ${fb.status === 'resolved' ? 'pill-good' : 'pill-muted'}`} style={{ marginLeft: 'auto' }}>
            {fb.status === 'resolved' ? '✅ Resolved · 已解决' : '🚫 Declined · 已驳回'}
            {fb.resolved_by_name ? ` · ${fb.resolved_by_name}` : ''}
          </span>
        )}
      </div>

      <p style={{ whiteSpace: 'pre-wrap', margin: '10px 0' }}>{fb.body}</p>

      {fb.screenshot && (
        <img src={fb.screenshot} alt="report screenshot" onClick={() => setZoom(z => !z)} className="shot-zoom"
          style={{ maxWidth: zoom ? '100%' : 320, border: '1px solid var(--rule)', borderRadius: 6, cursor: zoom ? 'zoom-out' : 'zoom-in', display: 'block' }} />
      )}

      {fb.disposition_note && <p className="muted small" style={{ marginTop: 8 }}>Note · 处理说明: {fb.disposition_note}</p>}

      {fb.status === 'open' && (
        <div className="row" style={{ gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Disposition note (optional) · 处理说明(可选)"
            style={{ flex: 1, minWidth: 220, fontSize: 13, padding: 6 }} />
          <button className="btn-good" disabled={resolve.isPending} onClick={() => resolve.mutate('resolved')}>✅ Resolve · 解决</button>
          <button disabled={resolve.isPending} onClick={() => resolve.mutate('declined')}>🚫 Decline · 驳回</button>
        </div>
      )}
      {resolve.isError && <div role="alert" style={{ color: 'var(--bad)', fontSize: 13, marginTop: 6 }}>Action failed — try again. · 操作失败,请重试。</div>}
    </div>
  );
}

export default function IssuesQueue() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>('open');
  const { data, isLoading } = useQuery({ queryKey: ['tool-feedback', status], queryFn: () => listToolFeedback(status) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['tool-feedback'] });

  const tab = (s: Status, label: string) => (
    <button type="button" onClick={() => setStatus(s)}
      className={`tab${status === s ? ' active' : ''}`} style={{ fontSize: 13 }}>
      {label}
    </button>
  );

  return (
    <div>
      <h2>🐞 Issues · 反馈队列</h2>
      <p className="lede">In-app bug reports and feature requests filed via the 💬 Report button. · 通过“反馈”按钮提交的问题与功能建议。</p>
      <div className="row" style={{ gap: 8, marginBottom: 14 }}>
        {tab('open', 'Open · 待处理')}
        {tab('resolved', 'Resolved · 已解决')}
        {tab('declined', 'Declined · 已驳回')}
      </div>

      {isLoading ? (
        <div className="card"><Skeleton lines={3} /></div>
      ) : !data || data.length === 0 ? (
        <EmptyState title="暂无内容 / Nothing here" hint={status === 'open' ? '队列清空了，干得漂亮。 / Queue is clear — nice work.' : undefined} />
      ) : (
        data.map(fb => <Row key={fb.id} fb={fb} onChanged={refresh} />)
      )}
    </div>
  );
}
